import React, { useState } from 'react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
} from '@radix-ui/react-dropdown-menu'
import { Button, ButtonText } from '../../components/ui/button'
import sidebar from '@/assets/svg/sidebar.svg'
import { ChevronDownIcon, CircleIcon, DotIcon } from 'lucide-react'
import {
  DropdownMenuContent,
  DropdownMenuItem,
} from '../../components/ui/DropdownMenu'
import {
  ArrowLeftStartOnRectangleIcon,
  ArrowRightCircleIcon,
  ArrowRightIcon,
  Cog6ToothIcon,
  DocumentMagnifyingGlassIcon,
  ListBulletIcon,
  PencilSquareIcon,
  PlusCircleIcon,
  SquaresPlusIcon,
  StarIcon,
} from '@heroicons/react/16/solid'
import useAppStore from '@/store/useAppStore'
import useGetCurrentUser from '@/services/api/auth/useGetCurrentUser'
import useAuthStore from '@/store/useAuthStore'
import useGetTeams from '@/services/api/team/useGetTeams'
import useLogout from '@/services/api/auth/useLogout'
import { Link, Navigate, useNavigate } from 'react-router-dom'
import useSwitchTeam from '@/services/api/team/useSwitchTeam'
import useSetDefaultTeam from '@/services/api/team/useSetDefaultTeam'
import EditTeam from './EditTeam'

export interface SideBarProps {
  // data: any
}

export default function SideBar() {
  const [openMenu, setOpenMenu] = React.useState(true)
  const { action: switchTeam, status: switchTeamStatus } = useSwitchTeam()

  const togglePanelState = useAppStore((state) => state.togglePanelState)
  const avatar = useAuthStore((state) => state.getAvatar())
  const currentTeamId = useAuthStore((state) => state.currentTeamId)
  const { data: teams } = useGetTeams()

  const { data: userData } = useGetCurrentUser()
  const { logout } = useLogout()
  const { action: setDefaultTeam, status: defaultTeamStatus } =
    useSetDefaultTeam()

  const navigate = useNavigate()

  async function handleLogout() {
    logout()
  }

  async function handleSwitchTeam(id) {
    navigate('/')
    await switchTeam({ teamId: id })
  }

  return (
    <>
      <div className="transition-width block h-screen shrink-0 border bg-gray-100 duration-300">
        <div className="flex items-center justify-between border p-3">
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger>
              <Button variant="ghost" className="px-1">
                <Button
                  variant="avatar"
                  className="h-[24px] rounded-full py-4"
                  onClick={togglePanelState}
                >
                  <ButtonText>{avatar}</ButtonText>
                </Button>
                <p className="ml-2 text-txt-primary">{userData?.name}</p>
                <ChevronDownIcon className="ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[220px]">
              <DropdownMenuItem asChild>
                <Link to={'/account'}>
                  <Cog6ToothIcon className="mr-2 size-4 text-txt-secondary" />
                  Settings
                </Link>
              </DropdownMenuItem>
              <DropdownMenuItem asChild>
                <a
                  href="https://www.profitflow.au/whats-new"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <SquaresPlusIcon className="mr-2 size-4 text-txt-secondary" />
                  What’s New
                </a>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleLogout}>
                <ArrowLeftStartOnRectangleIcon className="mr-2 size-4 text-txt-secondary" />
                Log Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <Button variant="ghost" iconOnly onClick={togglePanelState}>
            <img src={sidebar} alt="sidebar" />
          </Button>
        </div>

        <div className="p-4">
          <div className="flex items-center justify-between  text-sm font-medium  text-txt-secondary">
            <p>My Spaces</p>
            <div className="flex items-center">
              <Link to={'/create-space'}>
                <Button iconOnly variant="ghost">
                  <PlusCircleIcon />
                </Button>
              </Link>

              <Button iconOnly variant="ghost">
                <ChevronDownIcon onClick={() => setOpenMenu(!openMenu)} />
              </Button>
            </div>
          </div>
          {openMenu && (
            <ul className="">
              {teams?.map((team) => (
                <li
                  key={team.id}
                  data-loading={
                    [
                      defaultTeamStatus.status,
                      switchTeamStatus.status,
                    ].includes('pending')
                      ? true
                      : undefined
                  }
                  data-active={team.id === currentTeamId ? true : undefined}
                  className="group flex w-full items-center justify-between rounded-md p-2 text-sm font-medium  text-txt-primary hover:bg-gray-tr-08 data-[loading]:pointer-events-none data-[loading]:animate-pulse data-[active]:bg-green-tr-12 data-[active]:text-txt-success"
                >
                  <div className="grid grid-cols-[1.25rem,1fr]">
                    <div className="flex items-center justify-start">
                      {team.is_default ? (
                        <div>
                          <StarIcon className="size-4 text-blue-500" />
                        </div>
                      ) : (
                        <div className="flex">
                          <Button
                            className="hidden h-max w-max p-0 group-hover:block"
                            iconOnly
                            variant="ghost"
                            title="Make default"
                            type="button"
                            onClick={() => setDefaultTeam({ teamId: team.id })}
                          >
                            <StarIcon className="size-4 text-blue-500" />
                          </Button>
                          <div className="ml-[1px] size-3 rounded-full bg-gray-300 group-hover:hidden"></div>
                        </div>
                      )}
                    </div>
                    <label
                      className="overflow-hidden text-ellipsis whitespace-nowrap"
                      title={team.name}
                    >
                      {team.name}
                    </label>
                  </div>

                  <div className="hidden items-center group-hover:flex">
                    <EditTeam teamId={team.id} />

                    {currentTeamId !== team.id && (
                      <Button
                        className="h-max w-max p-[2px]"
                        iconOnly
                        variant="ghost"
                        title="Switch space"
                        onClick={() => handleSwitchTeam(team.id)}
                      >
                        <ArrowRightIcon />
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </>
  )
}
